// The builder API with the signed-in person's token.
import { bearer, signIn } from "./auth";
import { activeOrg, setActiveOrg } from "./org";

/** Authorization and the active organisation (X-Builder-Org) for fetch. */
export async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { Authorization: `Bearer ${await bearer()}` };
  const org = activeOrg();
  if (org) h["X-Builder-Org"] = org;
  return h;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await bearer();
  if (!token) {
    await signIn();
    throw new ApiError(401, "signing in");
  }
  const init: RequestInit = { method, headers: await authHeaders() };
  if (body instanceof FormData) init.body = body;
  else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
  }
  const r = await fetch(`/api/v1${path}`, init);
  if (r.status === 403 && activeOrg()) {
    // the remembered organisation is gone (left, removed, switched off):
    // forget it, the next call falls back to the first one
    const d = await r.clone().json().catch(() => null);
    if (d && typeof d.error === "string" && /not a member of this organisation|switched off/.test(d.error)) setActiveOrg("");
  }
  if (r.status === 401) {
    await signIn();
    throw new ApiError(401, "signing in");
  }
  if (r.status === 204) return undefined as T;
  const text = await r.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!r.ok) throw new ApiError(r.status, (data && data.error) || r.statusText);
  return data as T;
}

/** A raw text resource of the builder API (files of a revision). */
export async function apiText(path: string): Promise<string> {
  const r = await fetch(`/api/v1${path}`, { headers: await authHeaders() });
  if (!r.ok) throw new ApiError(r.status, r.statusText);
  return r.text();
}
